import React, { FC } from 'react';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import styles from '@/components/common/Explorer/Explorer.module.scss';

import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';

import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';

import useProductContext from '@/context/ProductContext';

import { TypeProduct, TypeTextCopy } from '@/core/generated-types';
import { TrustedShopReviews } from '@/components/ui';

import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';

import { RichTextHeading } from '../RichTextHeading';

import ExplorerSlice from './ExplorerSlice';
import ExplorerSwitch from './ExplorerSwitch';

const Explorer: FC<any> = ({
  title,
  text,
  slices,
  backgroundColor,
  anchorId: explorerAnchorId,
  titleAsH1 = false,
  showSliceAnchors = true,
  titleRichText,
  theme = 'default',
  columnsDescription,
  newAnchorStyle = false,
  enableFluidGrid = false,
  eventPageLocation,
}) => {
  const {
    enableYotpoReviewsSummary,
    enableTrustedShopsReviewsSummary,
    product,
  } = useProductContext();

  return (
    <>
      <div id={explorerAnchorId} className="relative -top-[90px]" />
      <div
        className={classNames(
          'flex justify-center pt-2 pb-8 lg:pb-10 lg:px-14',
          {
            [`bg-${backgroundColor?.fields?.color}`]:
              !!backgroundColor?.fields?.color,
          },
        )}
      >
        <div>
          <div className="px-5">
            {title &&
              (titleAsH1 ? (
                <Heading
                  tag={HeadingTag.H1}
                  size={HeadingSize.H1}
                  text={title}
                  align="center"
                  classes="pt-[50px] pb-5 md:pb-0"
                />
              ) : (
                <Heading
                  tag={HeadingTag.H2}
                  size={HeadingSize.H2}
                  align="center"
                  text={title}
                  hoverEffect
                />
              ))}
            {titleRichText && (
              <div
                className={classNames('text-center', styles.title)}
                data-testid="rich-text-heading"
              >
                <RichTextHeading content={titleRichText as Document} />
              </div>
            )}
            {shouldUseYotpoReviews() && enableYotpoReviewsSummary && (
              <div
                className={`plp-reviews-summary mt-2 flex justify-center ${styles.root}`}
              >
                <YotpoReviewStars product={product as TypeProduct} />
              </div>
            )}
            {shouldUseTrustedShopsReviews() &&
              enableTrustedShopsReviewsSummary && (
                <div className="text-d-base">
                  <TrustedShopReviews
                    widgetType="ratings-long"
                    shopifyProduct={null}
                  />
                </div>
              )}
            {columnsDescription && (
              <div className="grid grid-cols-2 gap-y-1 gap-x-4 lg:gap-y-3 lg:gap-x-16 place-items-center text-lg md:text-2xl">
                {columnsDescription.map((item: TypeTextCopy, index: number) => {
                  return (
                    <p
                      key={index}
                      className={`${index % 2 === 0 ? 'justify-self-end' : 'justify-self-start'}`}
                    >
                      {item.fields.text}
                    </p>
                  );
                })}
              </div>
            )}
            {text && (
              <p
                className={classNames('text-base md:text-lg md:text-center', {
                  'md:mb-6': enableFluidGrid,
                  'text-center md:max-w-[768px] md:mx-auto': newAnchorStyle,
                })}
              >
                {text}
              </p>
            )}
          </div>
          <div
            className={classNames('lg:px-5', {
              'sticky top-[72px] lg:top-[90px] z-10 bg-white': newAnchorStyle,
            })}
          >
            {showSliceAnchors && (
              <ExplorerSwitch
                slices={slices}
                theme={theme}
                newAnchorStyle={newAnchorStyle}
                enableFluidGrid={enableFluidGrid}
                eventPageLocation={eventPageLocation}
              />
            )}
          </div>
          <div
            className={classNames('px-5', {
              'lg:grid lg:grid-flow-col lg:gap-x-6 lg:grid-cols-[1fr_1fr_1fr]':
                enableFluidGrid,
            })}
          >
            {slices &&
              slices?.map(({ fields: slice }: any) => (
                <ExplorerSlice key={slice.anchorId} {...slice} />
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(Explorer);
